// Pure derivations for the admin dashboard (design/05 §6). The dashboard composes real data
// from three existing admin APIs — /admin/officers, /admin/recordings, /admin/dashboard — into
// operational summaries. Kept side-effect-free so the arithmetic is unit-tested without React
// or the network. The UI (AdminConsole.tsx) only renders these results.

export interface OfficerInput {
  available: boolean;
}

export interface RecordingInput {
  status: string;
  sizeBytes?: number;
  durationSec?: number;
  createdAt?: string;
  signedAt?: string;
}

export interface Kpi {
  name: string;
  value: number;
}

export interface OfficersSummary {
  total: number;
  available: number;
  unavailable: number;
}

export interface RecordingsSummary {
  total: number;
  completed: number;
  failed: number;
  /** queued + compositing + capturing — work still in the pipeline. */
  inProgress: number;
  signed: number;
  totalMinutes: number;
  totalBytes: number;
  latestCreatedAt?: string;
  statusCounts: Record<string, number>;
}

// Recording statuses that represent work still in progress (mirrors the he.rec.status* set).
const IN_PROGRESS_STATUSES = new Set(["queued", "compositing", "capturing"]);

export function summarizeOfficers(officers: OfficerInput[]): OfficersSummary {
  const available = officers.reduce((n, o) => n + (o.available ? 1 : 0), 0);
  return { total: officers.length, available, unavailable: officers.length - available };
}

export function summarizeRecordings(rows: RecordingInput[]): RecordingsSummary {
  let completed = 0, failed = 0, inProgress = 0, signed = 0, totalSec = 0, totalBytes = 0;
  let latestCreatedAt: string | undefined;
  const statusCounts: Record<string, number> = {};

  for (const r of rows) {
    const s = (r.status ?? "").toLowerCase();
    statusCounts[s] = (statusCounts[s] ?? 0) + 1;
    if (s === "completed") completed += 1;
    else if (s === "failed") failed += 1;
    else if (IN_PROGRESS_STATUSES.has(s)) inProgress += 1;

    if (r.signedAt) signed += 1;
    if (typeof r.durationSec === "number") totalSec += r.durationSec;
    if (typeof r.sizeBytes === "number") totalBytes += r.sizeBytes;
    // ISO-8601 UTC strings sort lexicographically, so max string = latest instant.
    if (r.createdAt && (!latestCreatedAt || r.createdAt > latestCreatedAt)) latestCreatedAt = r.createdAt;
  }

  return {
    total: rows.length,
    completed,
    failed,
    inProgress,
    signed,
    totalMinutes: Math.round(totalSec / 60),
    totalBytes,
    latestCreatedAt,
    statusCounts
  };
}

/** Most recent recordings first (by createdAt), capped at `limit`. Does not mutate the input. */
export function recentRecordings<T extends RecordingInput>(rows: T[], limit: number): T[] {
  return [...rows]
    .sort((a, b) => (b.createdAt ?? "").localeCompare(a.createdAt ?? ""))
    .slice(0, limit);
}

// Catalogue units keyed by KPI name (mirrors app-service KpiCatalogue.cs), so the monitoring
// section can render a value with the right unit even though the API sends only name+value.
export const KPI_UNITS: Record<string, string> = {
  vc_active_calls: "count",
  vc_configured_capacity: "count",
  vc_available_slots: "count",
  vc_capacity_utilization_ratio: "ratio",
  vc_call_setup_seconds_p95: "seconds",
  vc_call_failure_ratio: "ratio",
  vc_reconnect_ratio: "ratio",
  vc_compositing_queue_depth: "count",
  vc_compositing_latency_seconds_p95: "seconds",
  vc_compositing_failed_total: "counter",
  vc_recording_capture_faults_total: "counter",
  vc_scratch_store_free_ratio: "ratio",
  vc_recording_store_free_ratio: "ratio",
  vc_cpu_headroom_ratio: "ratio"
};

/** True when at least one raw KPI has actually reported a non-zero value. */
export function hasReportedMetrics(kpis: Kpi[]): boolean {
  return kpis.some((k) => Number(k.value) !== 0);
}

/** The KPIs worth surfacing in the secondary monitoring section (non-zero only). */
export function nonZeroKpis(kpis: Kpi[]): Kpi[] {
  return kpis.filter((k) => Number(k.value) !== 0);
}

/**
 * Format a raw KPI value for display by its catalogue unit (KpiCatalogue.cs). Ratios become a
 * whole-percent string (the "%" is the unit); seconds keep one decimal; counts are integers.
 * The Hebrew unit suffix for non-percent units is added by the UI.
 */
export function formatKpiValue(unit: string, value: number): string {
  if (unit === "ratio") return `${Math.round(value * 100)}%`;
  if (unit === "seconds") return Number.isInteger(value) ? String(value) : String(Math.round(value * 10) / 10);
  return String(value);
}
